import { useMemo, useSyncExternalStore, type FC } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, NakedButton, Tray } from '@frontend/design-system';
import { DemoPanelScopeProvider } from './demo-panel-context';
import { useDemoPanelShallowStore, type DemoPanelEntry, type DemoPanelHeaderConfig } from './demo-panel-store';

const DEFAULT_PANEL_WIDTH = 600;
const TRAY_MARGIN_PX = 80;

/** Subscribe to window resize events */
const subscribeWindowWidth = (callback: () => void) => {
  window.addEventListener('resize', callback, { passive: true });
  return () => window.removeEventListener('resize', callback);
};
const getWindowWidth = () => window.innerWidth;
const useWindowWidth = () => useSyncExternalStore(subscribeWindowWidth, getWindowWidth);

export const DemoPanelDrawer: FC = () => {
  const {
    panels,
    closeAll: handleClose,
    unregister,
    headerConfig,
  } = useDemoPanelShallowStore('panels', 'closeAll', 'unregister', 'headerConfig');

  const windowWidth = useWindowWidth();
  const totalPanelWidth = useMemo(() => panels.reduce((sum, p) => sum + (p.width ?? DEFAULT_PANEL_WIDTH), 0), [panels]);

  // Stack when all panels don't fit side-by-side with some margin
  const isStacked = totalPanelWidth + TRAY_MARGIN_PX > windowWidth;

  const isOpen = panels.length > 0;
  const rootPanel = panels[0] as DemoPanelEntry | undefined;
  const topPanel = panels[panels.length - 1] as DemoPanelEntry | undefined;

  const trayWidthPx = useMemo(() => {
    if (!isOpen) return 0;
    if (isStacked) return panels.at(-1)?.width ?? DEFAULT_PANEL_WIDTH;
    return panels.reduce((sum, p) => sum + (p.width ?? DEFAULT_PANEL_WIDTH), 0);
  }, [panels, isOpen, isStacked]);

  if (!isOpen || !rootPanel || !topPanel) {
    return null;
  }

  return (
    <Tray
      show={isOpen}
      onClose={handleClose}
      width='large'
      zIndex={theme.zIndex.modals - 1}
      css={[styles.tray, css({ width: trayWidthPx, maxWidth: isStacked ? '100vw' : '95vw' })]}
      showBackdrop
    >
      <DemoPanelHeader config={headerConfig} onClose={handleClose} />
      <div css={styles.body}>
        {panels.map((panel, index) => {
          const isTop = panel.id === topPanel.id && isStacked;
          const isVisible = !isStacked || isTop;
          const Component = panel.component;

          return (
            <div
              key={panel.id}
              css={[styles.panelSlot, index > 0 && styles.panelBorder, !isVisible && styles.hidden]}
              style={{
                width: isStacked ? '100%' : panel.width ?? DEFAULT_PANEL_WIDTH,
                minWidth: isStacked ? undefined : panel.width ?? DEFAULT_PANEL_WIDTH,
              }}
            >
              <DemoPanelScopeProvider isTopOfStack={isTop} close={() => unregister(panel.id)}>
                <Component {...(panel.componentProps as Record<string, unknown>)} />
              </DemoPanelScopeProvider>
            </div>
          );
        })}
      </div>
    </Tray>
  );
};

const DemoPanelHeader: FC<{
  config?: DemoPanelHeaderConfig;
  onClose: () => void;
}> = ({ config, onClose }) => {
  const { t } = useTranslation('base');
  if (!config) return null;

  return (
    <div css={styles.headerBanner}>
      <div css={styles.headerTitle}>
        {config.icon || (config.iconName ? <Icon name={config.iconName} /> : null)}
        {config.title ?? t('This is sample data.')}
      </div>
      <div css={styles.headerAction}>
        {config.action && 'render' in config.action ? (
          config.action.render
        ) : config.action ? (
          <Button variant='primary' onClick={config.action.onClick} trackingId={config.action.trackingId}>
            {config.action.title}
          </Button>
        ) : null}
      </div>
      <div css={styles.headerClose}>
        <NakedButton css={styles.closeBtn} trackingId='demo-panel-close-button' onClick={onClose}>
          <Icon name='x-small' />
        </NakedButton>
      </div>
    </div>
  );
};

const styles = {
  tray: css`
    padding: 0;
    gap: 0;
    transition: width 0.3s ease;
  `,
  body: css`
    display: flex;
    flex-direction: row-reverse;
    flex: 1;
    overflow: hidden;
    height: 100%;
  `,
  panelSlot: css`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden auto;
    flex-shrink: 0;
  `,
  panelBorder: css`
    border-right: 1px solid ${theme.colors.neutral20};
  `,
  hidden: css`
    display: none;
  `,
  headerBanner: css`
    display: grid;
    grid-template-areas: 'title action close';
    grid-template-columns: 1fr auto auto;
    padding: ${theme.spacing(2)};
    gap: ${theme.spacing(2)};
    align-items: center;
    background-color: ${theme.colorsV2.seaweed50(10)};
    border-bottom: 1px solid ${theme.colors.secondary.seaweed50};
    transition: width 0.3s ease;
  `,
  headerTitle: css`
    grid-area: title;
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  headerAction: css`
    grid-area: action;
  `,
  headerClose: css`
    grid-area: close;
  `,
  closeBtn: css`
    border-radius: ${theme.borderRadius.full};
    background-color: ${theme.colors.white};
    padding: ${theme.spacing(1)};
  `,
};
